import { apiClient } from './api';
import type {
  ProgramExercise,
  ProgramExercisesByDay,
  CreateProgramExerciseInput,
  UpdateProgramExerciseInput,
  ReplaceProgramExerciseInput,
  ProgramDayNames,
  ProgramDayWeekdays
} from './types';

export class ProgramExerciseService {
  
  static async getProgramExercises(programId: number): Promise<{
    programId: number;
    exercisesByDay: ProgramExercisesByDay;
    dayNames?: ProgramDayNames;
    dayWeekdays?: ProgramDayWeekdays;
  }> {
    const response = await apiClient.get<{
      programId: number;
      exercisesByDay: ProgramExercisesByDay;
      dayNames?: ProgramDayNames;
      dayWeekdays?: ProgramDayWeekdays;
    }>(`/api/training_programs/${programId}/exercises`);
    return response.data;
  }

  static async addExerciseToProgram(
    programId: number,
    exerciseData: CreateProgramExerciseInput
  ): Promise<{ id: number; message: string }> {
    const response = await apiClient.post<{ id: number; message: string }>(
      `/api/training_programs/${programId}/exercises`,
      exerciseData
    );
    return response.data;
  }

  static async updateProgramExercise(
    programId: number,
    exerciseId: number,
    updateData: UpdateProgramExerciseInput
  ): Promise<{ message: string }> {
    const response = await apiClient.patch<{ message: string }>(
      `/api/training_programs/${programId}/exercises/${exerciseId}`,
      updateData
    );
    return response.data;
  }

  static async removeExerciseFromProgram(
    programId: number,
    exerciseId: number
  ): Promise<{ message: string }> {
    const response = await apiClient.delete<{ message: string }>(
      `/api/training_programs/${programId}/exercises/${exerciseId}`
    );
    return response.data;
  }

  static async replaceExerciseInProgram(
    programId: number,
    exerciseId: number,
    replacementData: ReplaceProgramExerciseInput
  ): Promise<{
    message: string;
    oldExerciseId: number;
    newExerciseId: number
  }> {
    const response = await apiClient.put<{ message: string; oldExerciseId: number; newExerciseId: number }>(
      `/api/training_programs/${programId}/exercises/${exerciseId}/replace`,
      replacementData
    );
    return response.data;
  }

  // Utility methods for working with program exercises
  static async getExercisesByDay(
    programId: number, 
    dayOfWeek: number
  ): Promise<ProgramExercise[]> {
    const programData = await this.getProgramExercises(programId);
    return programData.exercisesByDay[dayOfWeek] || [];
  }

  static async reorderExercisesInDay(
    programId: number,
    _dayOfWeek: number,
    exerciseOrders: { exerciseId: number; newOrder: number }[]
  ): Promise<void> {
    await apiClient.post(
      `/api/training_programs/${programId}/exercises/reorder`,
      { orders: exerciseOrders.map(o => ({ exerciseId: o.exerciseId, orderInSession: o.newOrder })) }
    );
  }

  static async moveExerciseToDay(
    programId: number, 
    exerciseId: number, 
    newDayOfWeek: number
  ): Promise<{ message: string }> {
    return this.updateProgramExercise(programId, exerciseId, { 
      dayOfWeek: newDayOfWeek 
    });
  }

  // Helper method to get day names
  static getDayName(dayOfWeek: number): string {
    const days = [
      'Day 1', 'Day 2', 'Day 3', 'Day 4',
      'Day 5', 'Day 6', 'Day 7'
    ];
    return days[dayOfWeek - 1] || 'Unknown';
  }

  // Helper method to format exercise targets for display
  static formatExerciseTargets(exercise: ProgramExercise): string {
    const targets = [];
    
    if (exercise.targetSets && exercise.targetReps) {
      targets.push(`${exercise.targetSets} sets × ${exercise.targetReps} reps`);
    } else if (exercise.targetSets) {
      targets.push(`${exercise.targetSets} sets`);
    } else if (exercise.targetReps) {
      targets.push(`${exercise.targetReps} reps`);
    }
    
    if (exercise.targetWeight) {
      targets.push(`${exercise.targetWeight} kg`);
    }
    
    if (exercise.targetDuration) {
      const minutes = Math.floor(exercise.targetDuration / 60);
      const seconds = exercise.targetDuration % 60;
      if (minutes > 0) {
        targets.push(`${minutes}:${seconds.toString().padStart(2, '0')}`);
      } else {
        targets.push(`${seconds}s`);
      }
    }
    
    if (exercise.targetDistance) {
      targets.push(`${exercise.targetDistance} km`);
    }
    
    return targets.length > 0 ? targets.join(' • ') : 'No targets set';
  }
}

export default ProgramExerciseService;